<script setup lang="ts">
import { useVfm } from 'vue-final-modal';

import type { LocationQuery } from 'vue-router';

// icons
import { faMapMarked, faTrashAlt, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { faMapPin, faTimesCircle, faSearch } from '@fortawesome/pro-regular-svg-icons';
import { faTimes } from '@fortawesome/pro-light-svg-icons';

import { searchUiGuestAdult, searchUiGuestChild } from '@/locales/fixtures';

// components
import CiSkeletonDatepicker from '@/components/skeleton/CiSkeletonDatepicker.vue';
import CiLocationInput from '@/components/CiSearch/Location/CiLocationInput.vue';
import CiSearchModal from '@/components/CiSearch/CiSearchModal.vue';

import dayjs from '@/utils/day';

/********************
 * PROPS & EMITS     *
 ********************/
export interface CiSearchProps {
  collapsible?: boolean;
  isDetailPage?: boolean;
  isMobile?: boolean;
}
const props = withDefaults(defineProps<CiSearchProps>(), {
  collapsible: false,
  isDetailPage: false,
  isMobile: false,
});

/********************
 * COMPOSITIONS      *
 ********************/
// const router = useRouter();
const runtimeConfig = useRuntimeConfig();
const getRouteBaseName = useRouteBaseName();
const route = useRoute();
const searchStore = useSearchStore();
const vfm = useVfm();
const { $gettext, $ngettext, $pgettext } = useGettext();
const { openModal, closeModal } = useCiModal();
const { setCustomUrl, trackStructEvent } = useTracking();

const locationInputRef = ref<InstanceType<typeof CiLocationInput> | null>(null);
const {
  ages,
  childAgeMissing,
  childrenCount,
  clearInput,
  clearedMapSection,
  closeOpenType,
  datePickerValues,
  dateTouched,
  getChildAge,
  hasCampsite,
  hasLocation,
  i18nPromoCampsitesTitle,
  i18nSuggestionTitle,
  isAreaSearch,
  localChildrenCount,
  onAccommodationChange,
  onAccommodationSave,
  onChildAgeChange,
  onDateChange,
  onDateClear,
  onDateSave,
  onFlexChange,
  onLocationChange,
  onLocationClear,
  onLocationInputFocus,
  onLocationSelected,
  onPersonCountChange,
  onPressedEnter,
  onResultHighlightChange,
  onSave,
  onSearchSubmit,
  openOpenType,
  pointer,
  pointerBackward,
  pointerForward,
  searchHasChanged,
  showSuggestions,
} = useSearch({});

const {
  i18nAccommodationChoice,
  i18nFlexDayOne,
  i18nFlexDayThree,
  i18nFlexDayTwo,
  i18nFlexHeadline,
  i18nLabelPitch,
  i18nLabelRentals,
  i18nLocationArea,
  i18nPlaceholderDate,
  i18nPlaceholderLocation,
  i18nResetSelection,
} = useI18nSearchAvailabilities();

/********************
 * REFS & VARS       *
 ********************/
const routeBaseName = computed(() => {
  // return getRouteBaseName(router.currentRoute.value);
  return getRouteBaseName(route);
});

const dates = computed(() => {
  const start = searchStore.searchParams.from ? dayjs(searchStore.searchParams.from).format('D.M.') : null;
  const end = searchStore.searchParams.until ? dayjs(searchStore.searchParams.until).format('D.M.') : null;
  return [start, end];
});

const isMobileVersion = computed(() => {
  return props.isMobile;
});

const showASTooltip = computed(() => {
  return (
    !dateTouched.value
    && !dates.value[0]
    && !dates.value[1]
    && !searchStore.asTooltipClosed
    && routeBaseName.value !== 'index'
    && !props.isDetailPage
  );
});

/********************
 * WATCHER           *
 ********************/
watch(() => route.query, onQueryChange, { immediate: false });

// set focus on location input
watch(
  () => locationInputRef.value,
  (newValue) => {
    if (newValue) {
      nextTick(() => {
        newValue.setFocus();
      });
    }
  },
  { immediate: false },
);

watchEffect(() => {
  if (props.isMobile) {
    if (!vfm.get('CiSearchModal') && searchStore.searchOpenType) {
      openSearchModal();
    }
  } else if (vfm.get('CiSearchModal')) {
    closeModal('CiSearchModal');
  }
});

/********************
 * FUNCTIONS         *
 ********************/
function onQueryChange(newValue: LocationQuery) {
  searchStore.searchCollapsed = false;

  if (newValue?.area) {
    clearedMapSection.value = false;
  }
}

function getFormattedDate(type: string, date: string | null) {
  if (!date) {
    return;
  }

  return type === 'start' ? dayjs(date).format('D.M') : dayjs(date).format('D.M.YY');
}

function onMobileSearchClicked() {
  if (!props.isDetailPage) {
    searchStore.searchOpenType = 'location';
  } else {
    searchStore.searchOpenType = 'date';
  }
}

function onFlyoutClose(type: string) {
  if (props.isMobile) {
    return;
  }

  closeOpenType(type);
}

function openSearchModal() {
  openModal(
    {
      component: CiSearchModal,
      attrs: {
        onClose: () => {
          closeModal('CiSearchModal');
        },
      },
    },
    {
      contentTransition: 'translate-y-up',
      contentClass: 'w-full h-full',
    },
  );
}

function toggleSearch(status: boolean) {
  if (!searchStore.searchTouched) {
    onMobileSearchClicked();
  } else {
    searchStore.searchCollapsed = status;
  }
}

function asTooltipVisible() {
  const url = getCurrentURL(runtimeConfig.public.baseUrl, route);
  setCustomUrl(url);
  trackStructEvent({
    category: 'tooltip',
    action: 'impression',
    label: 'search__tooltip',
  });
}

function onAsTooltipClicked() {
  trackStructEvent({
    category: 'tooltip',
    action: 'click',
    label: 'search__tooltip',
  });
  openOpenType('date');
}

function onAsTooltipClosed() {
  trackStructEvent({
    category: 'tooltip',
    action: 'close',
    label: 'search__tooltip',
  });
  searchStore.asTooltipClosed = true;
}

function onSubmit(openType: string | null) {
  onSave(openType);
  onSearchSubmit();
}

defineExpose({
  openOpenType,
});
</script>

<template>
  <div ref="search" class="ci-search relative">
    <!-- display -->
    <div
      v-if="props.collapsible && !searchStore.searchCollapsed && isMobileVersion"
      class="flex min-h-[66px] cursor-pointer items-center rounded-[2rem] bg-white px-4 text-sm outline outline-[3px] outline-warning"
      @click="toggleSearch(true)"
    >
      <CiAwesomeIcon class="mr-4" :icon="faSearch" ratio="0.8" />
      <div class="flex flex-col truncate">
        <div class="search-display__location-date mb-1 flex min-w-0 flex-row">
          <div v-if="!isDetailPage" class="search-display__location min-w-0 shrink truncate">
            <span v-if="!searchStore.searchParams.q && !isAreaSearch" class="text-gray-light">
              {{ i18nPlaceholderLocation }}</span>
            <span v-if="!searchStore.searchParams.q && isAreaSearch">{{ i18nLocationArea }}</span>
            <span v-if="searchStore.searchParams.q && searchStore.searchParams.q !== ''">{{
              searchStore.searchParams.q
            }}</span>
          </div>

          <!-- prettier-ignore -->
          <div
            v-if="dates[0] || dates[1]"
            class="search-display__date min-w-0 shrink-0 whitespace-nowrap"
            :class="{ 'search-display__date--no-pipe': props.isDetailPage }"
          >
            <span v-if="searchStore.searchParams.flex">±</span>
            {{ dates[0] }} - {{ dates[1] }}
          </div>
          <div
            v-else
            class="search-display__date min-w-0 truncate whitespace-nowrap text-gray-light"
            :class="{ 'search-display__date--no-pipe': props.isDetailPage }"
          >
            {{ $gettext('Arrival - Departure') }}
          </div>
        </div>

        <div class="search-display__accommodation-guests flex flex-row">
          <div v-if="i18nAccommodationChoice" class="min-w-0 truncate">
            {{ i18nAccommodationChoice }}
          </div>
          <div class="search-display__guests whitespace-nowrap">
            <span v-if="childrenCount > 0">
              {{
                $gettext('%{ adults }+%{ children } Guests', {
                  adults: searchStore.searchParams.adults,
                  children: childrenCount,
                })
              }}
            </span>
            <span v-else>
              {{
                $ngettext('%{ adults } Guest', '%{ adults } Guests', searchStore.searchParams.adults, {
                  adults: searchStore.searchParams.adults,
                })
              }}
            </span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="(props.collapsible && searchStore.searchCollapsed) || !props.collapsible || !isMobileVersion"
      class="rounded-[2rem] bg-white outline outline-[3px] outline-warning"
    >
      <!-- mobile view on home -->
      <div
        v-if="routeBaseName === 'index' && isMobileVersion"
        class="flex h-[66px] cursor-pointer flex-row items-center p-4"
        @click="onMobileSearchClicked"
      >
        <CiAwesomeIcon :icon="faSearch" class="mr-4" ratio="0.8" />
        <span class="truncate">{{ i18nPlaceholderLocation }}</span>
      </div>

      <form
        v-else
        class="search__form relative flex min-h-[66px] w-full"
        :class="{
          'flex-row p-2': !isMobileVersion && !props.isDetailPage,
          'flex-col': isMobileVersion || props.isDetailPage,
        }"
        autocomplete="off"
        novalidate
        @keydown.down.prevent="pointerForward"
        @keydown.up.prevent="pointerBackward"
        @keypress.enter.prevent="onPressedEnter"
      >
        <client-only>
          <!-- collapsed button -->
          <div
            v-if="(searchStore.searchCollapsed && isMobileVersion) || props.isDetailPage"
            class="flex flex-row justify-between px-4 pb-3 pt-4"
          >
            <div class="flex flex-row items-center">
              <CiAwesomeIcon class="mr-4" :icon="faSearch" ratio="0.8" />
              <span v-if="props.isDetailPage" class="m-0 font-bold">
                {{ $pgettext('Search input headline on CPDP', 'Availabilities') }}</span>
              <span v-else class="m-0 font-bold">{{ $gettext('Change search') }}</span>
            </div>

            <CiButton
              tag="button"
              class="size-[34px]"
              :class="{ invisible: props.isDetailPage || (props.isDetailPage && isMobileVersion) }"
              variant="text"
              size="icon"
              @click="toggleSearch(false)"
            >
              <CiAwesomeIcon :icon="faTimesCircle" ratio="1" />
            </CiButton>
          </div>

          <!-- location -->
          <div
            v-if="!props.isDetailPage"
            class="search__location relative flex min-w-0 grow items-center"
            :class="{
              'border-y border-gray-30 py-2': isMobileVersion,
              'border-r border-gray-30 pl-2': !isMobileVersion,
            }"
          >
            <CiFlyout
              class="grow"
              :align="isMobileVersion ? 'center' : 'left'"
              :name="'location'"
              :open="!isMobileVersion && searchStore.searchOpenType === 'location'"
              flyout-classes="flyout__location"
              @close="onFlyoutClose"
            >
              <div
                class="flex w-full cursor-pointer flex-col px-4 text-sm leading-[21px]"
                data-testid="ci-search-flyout--location"
                tabindex="0"
                role="button"
                @click="openOpenType('location')"
                @keydown.enter.prevent="openOpenType('location')"
              >
                <!-- label-wrapper -->
                <div class="mb-1 flex items-center font-medium">
                  <span class="truncate">{{
                    $pgettext('Search input choice label location', 'Camping location')
                  }}</span>
                </div>

                <!-- search value -->
                <div class="truncate focus:outline-none">
                  <span v-if="!searchStore.searchParams.q && !isAreaSearch" class="text-gray-light">
                    {{ i18nPlaceholderLocation }}</span>
                  <span v-if="!searchStore.searchParams.q && isAreaSearch">{{ i18nLocationArea }}</span>
                  <span v-if="searchStore.searchParams.q && searchStore.searchParams.q !== ''">{{
                    searchStore.searchParams.q
                  }}</span>
                </div>
              </div>

              <template #headline>
                <div class="flyout__headline m-0 font-medium">
                  {{ $pgettext('Search flyout headline location', 'Where do you want to camp?') }}
                </div>
              </template>
              <template #content>
                <!-- search value -->
                <div class="px-4 pb-2">
                  <CiLocationInput
                    ref="locationInputRef"
                    :search-string="searchStore.searchParams.q"
                    :is-submit="searchStore.searchTouched"
                    @focus="onLocationInputFocus"
                    @change="onLocationChange"
                    @clear="onLocationClear"
                    @submit="onLocationSelected"
                  />
                </div>

                <div class="px-0">
                  <CiSearchResultList
                    v-if="showSuggestions && hasLocation"
                    :active="pointer"
                    :results="searchStore.suggestions.locations"
                    :title="''"
                    :icon="faMapMarked"
                    title-prop="name"
                    subtitle-prop="subtitle"
                    @search-result-list-click="onLocationSelected"
                    @highlight-change="onResultHighlightChange"
                  />

                  <CiSearchResultList
                    v-if="showSuggestions && hasCampsite"
                    :active="pointer"
                    :index-offset="searchStore.suggestions.locations.length"
                    :results="searchStore.suggestions.campsites"
                    :title="searchStore.searchParams.q?.length > 2 ? i18nSuggestionTitle : i18nPromoCampsitesTitle"
                    :icon="faMapPin"
                    :no-results-message="true"
                    title-prop="name"
                    subtitle-prop="campsite"
                    image-prop="icon_8_5"
                    @search-result-list-click="onLocationSelected"
                    @highlight-change="onResultHighlightChange"
                  />
                </div>
              </template>
            </CiFlyout>

            <!-- clear -->
            <CiButton
              v-if="searchStore.searchParams.q !== '' || (searchStore.searchParams.q === '' && isAreaSearch)"
              tag="button"
              variant="text"
              class="search__clear"
              @click.stop="clearInput([{ q: '', area: isAreaSearch }])"
            >
              <CiAwesomeIcon :icon="faTimesCircle" ratio="0.7" />
              Clear Search
            </CiButton>
          </div>

          <!-- date / accommodation & traveller / submit -->
          <div
            class="search__wrapper flex items-center"
            :class="{
              'flex-row': !isMobileVersion && !props.isDetailPage,
              'flex-col': isMobileVersion || props.isDetailPage,
            }"
          >
            <!-- date -->
            <div
              class="group/search-date search__date relative flex h-full items-center"
              :class="{
                'search__date--large border-r border-gray-30': !isMobileVersion && !props.isDetailPage,
                'border-b border-gray-30 py-2': isMobileVersion || props.isDetailPage,
                'border-t border-gray-30': props.isDetailPage,
              }"
            >
              <CiFlyout
                class="w-full"
                :align="props.isDetailPage ? 'left' : 'center'"
                :name="'date'"
                :open="!isMobileVersion && searchStore.searchOpenType === 'date'"
                :flyout-classes="props.isDetailPage ? 'flyout__date flyout__date--detail' : 'flyout__date'"
                @close="onFlyoutClose"
              >
                <div
                  class="flex w-full cursor-pointer flex-col px-4 text-sm leading-[21px]"
                  data-testid="ci-search-flyout--date"
                  tabindex="0"
                  role="button"
                  @click="openOpenType('date')"
                  @keydown.enter.prevent="openOpenType('date')"
                >
                  <!-- label-wrapper -->
                  <div class="mb-1 flex items-center font-medium">
                    <span class="truncate">{{ $pgettext('Search input choice label dates', 'Travel period') }}</span>
                  </div>

                  <!-- search value -->
                  <div class="truncate focus:outline-none">
                    <span
                      v-if="!searchStore.searchParams.from && !searchStore.searchParams.until"
                      class="text-gray-light"
                    >
                      {{ i18nPlaceholderDate }}</span>
                    <span v-else>
                      <span v-if="searchStore.searchParams.flex">±</span>
                      {{ getFormattedDate('start', searchStore.searchParams.from) }} -
                      {{ getFormattedDate('end', searchStore.searchParams.until) }}
                    </span>
                  </div>
                </div>
                <template #headline>
                  <div class="flyout__headline m-0 font-medium">
                    {{ $pgettext('Search flyout headline date', 'When do you want to camp?') }}
                  </div>
                </template>
                <template #content>
                  <div class="w-full overflow-hidden">
                    <Suspense>
                      <LazyCiDatepicker :value="datePickerValues" @change="onDateChange" />
                      <template #fallback>
                        <CiSkeletonDatepicker />
                      </template>
                    </Suspense>
                  </div>

                  <div class="mt-2 flex flex-col gap-4 divide-y divide-gray-30 px-4">
                    <transition name="translate-y-up" mode="out-in">
                      <div v-if="datePickerValues[0] && datePickerValues[1]" class="flex flex-col gap-4">
                        <div>
                          <div class="flyout__headline m-0 font-medium">
                            {{ i18nFlexHeadline }}
                          </div>
                          <div class="flex pt-2">
                            <CiFilterBoolean
                              class="m-0 mr-1"
                              :value="searchStore.searchParams.flex === 1"
                              :name="'1'"
                              :label="i18nFlexDayOne"
                              @input="onFlexChange($event, 1)"
                            />
                            <CiFilterBoolean
                              class="m-0 mx-1"
                              :value="searchStore.searchParams.flex === 2"
                              :name="'2'"
                              :label="i18nFlexDayTwo"
                              @input="onFlexChange($event, 2)"
                            />
                            <CiFilterBoolean
                              class="m-0 ml-1"
                              :value="searchStore.searchParams.flex === 3"
                              :name="'3'"
                              :label="i18nFlexDayThree"
                              @input="onFlexChange($event, 3)"
                            />
                          </div>
                        </div>
                      </div>
                    </transition>

                    <div v-if="datePickerValues[0] || datePickerValues[1]">
                      <CiButton
                        tag="button"
                        class="mt-4"
                        variant="danger"
                        @click.stop="onDateClear"
                      >
                        <CiAwesomeIcon class="mr-2" :icon="faTrashAlt" ratio="0.6" />
                        {{ i18nResetSelection }}
                      </CiButton>
                    </div>
                  </div>

                  <div class="flex justify-between p-4">
                    <CiButton tag="button" variant="text" @click.stop="openOpenType('accommodation')">
                      {{ $pgettext('Search flyout action next', 'Next') }}
                    </CiButton>
                    <CiButton
                      v-if="searchStore.searchOpenType !== 'traveller' && !searchStore.searchTouched"
                      tag="button"
                      variant="primary"
                      @click.stop="onDateSave"
                    >
                      {{ $pgettext('Search flyout action next', 'Next') }}
                      <CiAwesomeIcon class="ml-2" :icon="faChevronRight" ratio="0.6" />
                    </CiButton>
                    <CiButton
                      v-else
                      tag="button"
                      variant="warning"
                      class="ml-auto"
                      data-testid="ci-search-flyout--date-submit"
                      @click.prevent="onSubmit(searchStore.searchOpenType)"
                    >
                      <CiAwesomeIcon class="mr-2" :icon="faSearch" ratio="0.6" />
                      {{ $pgettext('Search button text', 'Search') }}
                    </CiButton>
                  </div>
                </template>
              </CiFlyout>

              <!-- clear -->
              <CiButton
                v-if="searchStore.searchParams.from || searchStore.searchParams.until"
                tag="button"
                variant="text"
                class="search__clear"
                @click.stop="clearInput([{ from: null, until: null, flex: null }])"
              >
                <CiAwesomeIcon :icon="faTimesCircle" ratio="0.7" />
                Clear Search
              </CiButton>
            </div>

            <!-- accommodation & traveller -->
            <div
              class="flex h-full"
              :class="{
                'w-full flex-row': isMobileVersion && !props.isDetailPage,
                'w-full flex-col': props.isDetailPage,
              }"
            >
              <!-- accommodation -->
              <div
                class="search__accommodation relative flex items-center"
                :class="{
                  'search__accommodation--large items-center border-r border-gray-30 ':
                    !isMobileVersion && !props.isDetailPage,
                  'items-center border-r border-gray-30 py-2': isMobileVersion && !props.isDetailPage,
                  'items-center border-b border-r border-gray-30':
                    isMobileVersion && searchHasChanged && !props.isDetailPage,
                  '!w-full border-b border-gray-30 py-2': props.isDetailPage,
                }"
              >
                <CiFlyout
                  :align="props.isDetailPage ? 'left' : 'center'"
                  :name="'accommodation'"
                  :open="!isMobileVersion && searchStore.searchOpenType === 'accommodation'"
                  class="w-full"
                  flyout-classes="flyout__accommodation"
                  @close="onFlyoutClose"
                >
                  <div
                    class="flex w-full cursor-pointer flex-col px-4 text-sm leading-[21px]"
                    tabindex="0"
                    role="button"
                    @click="openOpenType('accommodation')"
                    @keydown.enter.prevent="openOpenType('accommodation')"
                  >
                    <!-- label-wrapper -->
                    <div class="mb-1 flex items-center font-medium">
                      <span class="truncate">
                        {{ $pgettext('Search input choice label accommodation', 'Accommodation type') }}
                      </span>
                    </div>

                    <!-- search value -->
                    <div
                      class="truncate focus:outline-none"
                      :class="{ 'text-gray': !searchStore.searchTouched }"
                    >
                      <span>{{ i18nAccommodationChoice }}</span>
                    </div>
                  </div>
                  <template #headline>
                    <div class="flyout__headline m-0 font-medium">
                      {{ $pgettext('Search flyout headline accommodation', 'How would you like to camp?') }}
                    </div>
                  </template>
                  <template #content>
                    <div class="px-4 pb-4">
                      <CiFilterBoolean
                        class="m-0"
                        :value="searchStore.searchParams.acco_type === 'pitch'
                          || searchStore.searchParams.acco_type === 'all'
                        "
                        :name="i18nLabelPitch"
                        :label="i18nLabelPitch"
                        @input="onAccommodationChange($event, 'pitch')"
                      >
                        <img src="~/assets/svg/icon-pitch.svg" class="mr-4" :alt="`icon ${i18nLabelPitch}`" />
                      </CiFilterBoolean>
                      <CiFilterBoolean
                        class="m-0 mt-4"
                        :value="searchStore.searchParams.acco_type === 'acco'
                          || searchStore.searchParams.acco_type === 'all'
                        "
                        :name="i18nLabelRentals"
                        :label="i18nLabelRentals"
                        @input="onAccommodationChange($event, 'acco')"
                      >
                        <img src="~/assets/svg/icon-cabin.svg" class="mr-4" :alt="`icon ${i18nLabelRentals}`" />
                      </CiFilterBoolean>
                      <div class="mt-4 flex justify-between">
                        <CiButton tag="button" variant="text" @click.stop="openOpenType('traveller')">
                          {{ $pgettext('Search flyout action next', 'Next') }}
                        </CiButton>
                        <CiButton
                          v-if="searchStore.searchOpenType !== 'traveller' && !searchStore.searchTouched"
                          tag="button"
                          variant="primary"
                          data-cy="ci-search-flyout--accommodation-submit"
                          @click.stop="onAccommodationSave"
                        >
                          {{ $pgettext('Search flyout action next', 'Next') }}
                          <CiAwesomeIcon class="ml-2" :icon="faChevronRight" ratio="0.6" />
                        </CiButton>
                        <CiButton
                          v-else
                          tag="button"
                          variant="warning"
                          class="ml-auto"
                          data-testid="ci-search-flyout--date-submit"
                          @click.prevent="onSubmit(searchStore.searchOpenType)"
                        >
                          <CiAwesomeIcon class="mr-2" :icon="faSearch" ratio="0.6" />
                          {{ $pgettext('Search button text', 'Search') }}
                        </CiButton>
                      </div>
                    </div>
                  </template>
                </CiFlyout>

                <!-- clear -->
                <CiButton
                  v-if="searchStore.searchParams.acco_type !== null && searchStore.searchParams.acco_type !== 'all'"
                  tag="button"
                  variant="text"
                  class="search__clear"
                  @click.stop="clearInput([{ acco_type: null }])"
                >
                  <CiAwesomeIcon :icon="faTimesCircle" ratio="0.7" />
                  Clear Search
                </CiButton>
              </div>

              <!-- traveller -->
              <div
                class="search__guests relative flex items-center"
                :class="{
                  'search__guests--large': !isMobileVersion && !props.isDetailPage,
                  'py-2': isMobileVersion || props.isDetailPage,
                  '!w-full': props.isDetailPage,
                  'border-b border-gray-30':
                    (props.isDetailPage && searchHasChanged)
                    || (props.isDetailPage && !isMobileVersion)
                    || (!props.isDetailPage && searchHasChanged && isMobileVersion),
                }"
              >
                <CiFlyout
                  class="w-full"
                  :align="isMobileVersion ? 'center' : props.isDetailPage ? 'left' : 'right'"
                  :name="'traveller'"
                  :open="!isMobileVersion && searchStore.searchOpenType === 'traveller'"
                  flyout-classes="flyout__guests"
                  @close="onFlyoutClose"
                >
                  <div
                    class="flex w-full cursor-pointer flex-col px-4 text-sm leading-[21px]"
                    tabindex="0"
                    role="button"
                    @click="openOpenType('traveller')"
                    @keydown.enter.prevent="openOpenType('traveller')"
                  >
                    <!-- label-wrapper -->
                    <div class="mb-1 flex items-center font-medium">
                      <span class="truncate">
                        {{ $pgettext('Search input choice label guests', 'Camper') }}
                      </span>
                    </div>

                    <!-- search value -->
                    <div
                      class="truncate"
                      :class="{ 'text-gray': !searchStore.searchTouched }"
                    >
                      <span v-if="childrenCount > 0" key="adultsAndChildren">
                        {{
                          $gettext('%{ adults }+%{ children } Guests', {
                            adults: searchStore.searchParams.adults,
                            children: childrenCount,
                          })
                        }}
                      </span>
                      <span v-else key="adults">
                        {{
                          $ngettext('%{ adults } Guest', '%{ adults } Guests', searchStore.searchParams.adults, {
                            adults: searchStore.searchParams.adults,
                          })
                        }}
                      </span>
                    </div>
                  </div>
                  <template #headline>
                    <div class="flyout__headline m-0 font-medium">
                      {{ $pgettext('Search flyout headline traveller', 'How many people would like to camp?') }}
                    </div>
                  </template>
                  <template #content>
                    <div class="flex flex-col px-4 pb-4">
                      <!-- guests -->
                      <CiGuest
                        class="mb-4"
                        :amount="searchStore.searchParams.adults"
                        :guest="searchUiGuestAdult"
                        :max="20"
                        :min="1"
                        @change="onPersonCountChange"
                      >
                        <img
                          src="~/assets/svg/icon-parents.svg"
                          width="30"
                          height="30"
                          alt="Adults"
                        />
                      </CiGuest>
                      <CiGuest
                        :amount="localChildrenCount"
                        :guest="searchUiGuestChild"
                        :max="20"
                        :min="0"
                        @change="onPersonCountChange"
                      >
                        <img
                          src="~/assets/svg/icon-children.svg"
                          width="25"
                          height="25"
                          alt="Children"
                        />
                      </CiGuest>

                      <!-- age -->
                      <div v-if="localChildrenCount > 0" class="mt-12 w-1/2">
                        <div class="flyout__headline m-0 font-medium">
                          {{ $gettext('Age of the children on the day of arrival?') }}
                        </div>

                        <template v-for="(_, idx) in localChildrenCount" :key="idx">
                          <div v-if="!getChildAge(idx)?.key && childAgeMissing" class="-mb-2 mt-2 text-sm text-danger">
                            {{ $gettext('Please specify age') }}
                          </div>
                          <CiSelect
                            :name="`childAge-${idx}`"
                            :options="ages"
                            class="mt-3 block w-full"
                            :value="getChildAge(idx)"
                            show-disabled-option
                            @change="onChildAgeChange($event, idx)"
                          />
                        </template>
                      </div>

                      <!-- save -->
                      <CiButton
                        tag="button"
                        variant="warning"
                        class="mt-12 w-full"
                        data-cy="ci-search-flyout--search-submit"
                        @focus="closeOpenType"
                        @click="onSearchSubmit"
                      >
                        <CiAwesomeIcon :icon="faSearch" ratio="0.6" class="mr-2" />
                        {{ $pgettext('Search button text', 'Search') }}
                      </CiButton>
                    </div>
                  </template>
                </CiFlyout>

                <!-- clear -->
                <CiButton
                  v-if="!(searchStore.searchParams.adults === 2 && searchStore.searchParams.children === null)"
                  tag="button"
                  variant="text"
                  class="search__clear"
                  @click.stop="clearInput([{ adults: 2, children: null }])"
                >
                  <CiAwesomeIcon :icon="faTimesCircle" ratio="0.7" />
                  Clear Search
                </CiButton>
              </div>
            </div>
          </div>
        </client-only>
        <!-- submit -->
        <CiButton
          v-if="!isMobileVersion || searchHasChanged"
          tag="button"
          type="submit"
          variant="warning"
          :size="!isMobileVersion && !props.isDetailPage ? 'icon' : 'default'"
          :class="{
            'max-w-none shrink-0 !border-0 md:ml-auto': !isMobileVersion && !props.isDetailPage,
            'icon-absolute m-4': isMobileVersion || props.isDetailPage,
          }"
          data-cy="ci-search--submit"
          @focus="closeOpenType()"
          @click.prevent="onSearchSubmit"
        >
          <CiAwesomeIcon
            class="mr-2"
            :class="{ '!mr-0': !isMobileVersion && !props.isDetailPage }"
            :icon="faSearch"
            :ratio="'0.6'"
          />
          <template v-if="props.isDetailPage">
            {{ $pgettext('Search button text', 'Search') }}
          </template>
          <span v-else :class="{ 'sr-only': !isMobileVersion && !props.isDetailPage }">{{
            $pgettext('Search button text', 'Search')
          }}</span>
        </CiButton>
      </form>
    </div>
    <client-only>
      <div
        v-if="showASTooltip"
        v-observe-visibility="{
          callback: asTooltipVisible,
          once: true,
          throttle: 800,
          intersection: {
            threshold: 0.5,
          },
        }"
        class="search__tooltip absolute bottom-[-15px] left-1/2 z-10 w-[90%] max-w-[300px] -translate-x-1/2 translate-y-full cursor-pointer rounded-lg bg-info p-4 pr-10 text-white md:w-auto"
        style="box-shadow: 0 0.063rem 0.313rem 0.063rem rgba(0, 0, 0, 0.25)"
        @click="onAsTooltipClicked"
      >
        <span>{{ $gettext('Select travel period to see current prices and offers') }}</span>
        <div class="absolute right-[10px] top-[10px]" @click.stop="onAsTooltipClosed">
          <CiAwesomeIcon class="fill-white" :icon="faTimes" ratio="0.8" />
        </div>
      </div>
    </client-only>
  </div>
</template>

<style lang="postcss" scoped>
.ci-search {

  .search__accommodation,
  .search__date,
  .search__location,
  .search__guests {
    .no-touch &:hover {
      .search__clear {
        @apply opacity-40 w-0;
        padding: 1rem !important;
      }
    }
  }

  .search__clear {
    @apply rounded-none size-0 overflow-hidden opacity-0 border-0 transition-opacity duration-200 ease-out;
    font-size: 0;
    padding: 0 !important;

    .no-touch &:hover {
      opacity: 0.6 !important;
    }
  }

  /* flyout */
  :deep(.flyout__inner) {
    @apply w-[768px] md:w-auto;
  }

  :deep(.flyout__headline) {
    @apply whitespace-normal sm:whitespace-nowrap;
    font-size: 0.875rem;
  }

  @screen md {
    .search__location :deep(.flyout__location) {
      min-width: 500px;
      max-width: 500px;
      left: -25px;
    }

    :deep(.flyout__date) {
      max-width: 500px;
      width: 500px;
    }

    :deep(.flyout__date--detail) {
      max-width: 650px;
      width: 650px;
    }
  }

  @screen xl {
    :deep(.flyout__date) {
      max-width: 800px;
      width: 800px;
    }

    :deep(.flyout__date--detail) {
      max-width: 850px;
      width: 850px;
    }
  }
}

.search__date {
  width: 100%;

  &--large {
    width: 155px;
  }
}

.search__accommodation {
  width: 50%;

  &--large {
    width: 190px;
  }
}

.search__guests {
  width: 50%;

  &--large {
    width: 120px;
  }
}

.search-display__date,
.search-display__guests {
  padding: 0;

  &::before {
    font-weight: bold;
    content: '|';
    padding: 0 4px;
  }
}

.search-display__date--no-pipe {
  &::before {
    content: '';
    padding: 0;
  }
}

.search__tooltip {
  &::before {
    border-color: transparent transparent rgb(var(--color-info)) transparent;
    border-style: solid;
    border-width: 10px;
    bottom: 100%;
    /* At the top of the tooltip */
    content: ' ';
    left: 50%;
    position: absolute;
    transform: translateX(-50%);
  }
}
</style>
